import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import {
  CreateIntegrationSchema,
  IntegrationProviderLabels,
  IntegrationProviderSchema,
  type CreateIntegration,
  type Integration,
  type IntegrationLocationsMapping,
  type IntegrationProvider,
  type IntegrationStatus,
  type UpdateIntegration,
  type VerifyIntegrationTokenResponse,
} from "@lumika/shared";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
  useZodForm,
} from "@lumika/ui";
import { FormField, Input } from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { formatDateTime } from "../../lib/format";

// Read-only sync-status badge styling. `status` is driven by the cron;
// the operator controls on/off via the `enabled` toggle, not this.
const STATUS_VARIANT: Record<
  IntegrationStatus,
  "default" | "success" | "warning" | "destructive"
> = {
  synced: "success",
  syncing: "default",
  backfilling: "warning",
  errored: "destructive",
};

/** Sentinel for the location-scope select — maps to rooftopId=null. */
const ALL_LOCATIONS = "__all__";

export function DealershipIntegrationsPage(): JSX.Element {
  const { dealershipId = "" } = useParams<{ dealershipId: string }>();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const {
    data: integrations,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["integrations", dealershipId],
    queryFn: () => adminApi.listIntegrations(dealershipId),
    enabled: Boolean(dealershipId),
  });

  // Rooftop names for the Location column.
  const rooftopsQuery = useQuery({
    queryKey: ["dealership-rooftops", dealershipId],
    queryFn: () => adminApi.listRooftops(dealershipId),
    enabled: Boolean(dealershipId),
  });
  const rooftopNameById = new Map(
    (rooftopsQuery.data ?? []).map((r) => [r.id, r.name]),
  );

  const removeMutation = useMutation({
    mutationFn: (integrationId: string) =>
      adminApi.removeIntegration(dealershipId, integrationId),
    onSuccess: () => {
      toast.success("Integration removed.");
      queryClient.invalidateQueries({ queryKey: ["integrations", dealershipId] });
    },
    onError: (err: unknown) => {
      toast.error(
        `Remove failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      integrationId,
      body,
    }: {
      integrationId: string;
      body: UpdateIntegration;
    }) => adminApi.updateIntegration(dealershipId, integrationId, body),
    onSuccess: () => {
      toast.success("Integration updated.");
      queryClient.invalidateQueries({ queryKey: ["integrations", dealershipId] });
    },
    onError: (err: unknown) => {
      toast.error(
        `Update failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    },
  });

  const resyncMutation = useMutation({
    mutationFn: (integrationId: string) =>
      adminApi.resyncIntegration(dealershipId, integrationId),
    onSuccess: () => {
      toast.success("Re-sync requested.");
      queryClient.invalidateQueries({ queryKey: ["integrations", dealershipId] });
    },
    onError: (err: unknown) => {
      toast.error(
        `Re-sync failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    },
  });

  if (isLoading) return <PageLoading />;
  if (error)
    return (
      <PageError
        title="Failed to load integrations"
        description={error instanceof Error ? error.message : String(error)}
      />
    );

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Integrations"
        description="Connect Space Auto and ADF lead destinations for this dealership."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4 mr-1" /> Add integration
          </Button>
        }
      />

      <Section>
        {!integrations || integrations.length === 0 ? (
          <EmptyState
            title="No integrations yet"
            description="Connect Space Auto for two-way sync, or ADF to forward new leads to another CRM by email."
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Token / Address</TableHead>
                    <TableHead>Enabled</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last pulled</TableHead>
                    <TableHead>Anon</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {integrations.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell>
                        <Badge>{IntegrationProviderLabels[i.provider]}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {i.rooftopId
                          ? (rooftopNameById.get(i.rooftopId) ?? i.rooftopId)
                          : "All locations"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {i.tokenMasked}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={i.enabled}
                          disabled={updateMutation.isPending}
                          onCheckedChange={(v) =>
                            updateMutation.mutate({
                              integrationId: i.id,
                              body: { enabled: v },
                            })
                          }
                          aria-label={i.enabled ? "Enabled" : "Disabled"}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[i.status]}>
                          {i.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {i.lastPulledAt ? formatDateTime(i.lastPulledAt) : "—"}
                        {i.lastPulledError ? (
                          <div className="text-red-600 mt-1" title={i.lastPulledError}>
                            {i.lastPulledError.length > 80
                              ? `${i.lastPulledError.slice(0, 80)}…`
                              : i.lastPulledError}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {i.includeUnauthorized ? "yes" : "no"}
                      </TableCell>
                      <TableCell className="text-xs max-w-[260px] truncate">
                        {i.notes ?? ""}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Force re-sync"
                          disabled={resyncMutation.isPending}
                          onClick={() => resyncMutation.mutate(i.id)}
                        >
                          <RefreshCw className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Remove"
                          disabled={removeMutation.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                `Remove ${IntegrationProviderLabels[i.provider]} integration?`,
                              )
                            ) {
                              removeMutation.mutate(i.id);
                            }
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {integrations.map((i) => (
                <MobileListRow
                  key={i.id}
                  primary={IntegrationProviderLabels[i.provider]}
                  secondary={
                    <span className="font-mono">{i.tokenMasked}</span>
                  }
                  tertiary={
                    <>
                      Last pulled:{" "}
                      {i.lastPulledAt ? formatDateTime(i.lastPulledAt) : "—"}
                      {i.lastPulledError ? (
                        <span className="block text-red-600" title={i.lastPulledError}>
                          {i.lastPulledError.length > 80
                            ? `${i.lastPulledError.slice(0, 80)}…`
                            : i.lastPulledError}
                        </span>
                      ) : null}
                      {i.notes ? (
                        <span className="block">{i.notes}</span>
                      ) : null}
                    </>
                  }
                  meta={
                    <Badge variant={i.includeUnauthorized ? "outline" : "secondary"}>
                      Anon: {i.includeUnauthorized ? "yes" : "no"}
                    </Badge>
                  }
                  trailing={
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={i.enabled}
                        disabled={updateMutation.isPending}
                        onCheckedChange={(v) =>
                          updateMutation.mutate({
                            integrationId: i.id,
                            body: { enabled: v },
                          })
                        }
                        aria-label={i.enabled ? "Enabled" : "Disabled"}
                      />
                      <Badge variant={STATUS_VARIANT[i.status]}>
                        {i.status}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Force re-sync"
                        disabled={resyncMutation.isPending}
                        onClick={() => resyncMutation.mutate(i.id)}
                      >
                        <RefreshCw className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Remove"
                        disabled={removeMutation.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `Remove ${IntegrationProviderLabels[i.provider]} integration?`,
                            )
                          ) {
                            removeMutation.mutate(i.id);
                          }
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      <CreateIntegrationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        dealershipId={dealershipId}
        onCreated={(row) => {
          queryClient.invalidateQueries({
            queryKey: ["integrations", dealershipId],
          });
          toast.success(
            `Created ${IntegrationProviderLabels[row.provider]} integration.`,
          );
        }}
      />
    </div>
  );
}

function CreateIntegrationDialog({
  open,
  onOpenChange,
  dealershipId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  dealershipId: string;
  onCreated: (i: Integration) => void;
}): JSX.Element {
  const form = useZodForm(CreateIntegrationSchema, {
    provider: "spaceauto" as IntegrationProvider,
    token: "",
    notes: "",
    includeUnauthorized: false,
    startSyncAt: null,
  });

  const [verified, setVerified] = useState<
    VerifyIntegrationTokenResponse["dealership"] | null
  >(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  // Location scope — a specific rooftop, or the whole dealership.
  const [scopeRooftopId, setScopeRooftopId] = useState<string>(ALL_LOCATIONS);

  const rooftopsQuery = useQuery({
    queryKey: ["dealership-rooftops", dealershipId],
    queryFn: () => adminApi.listRooftops(dealershipId),
    enabled: open && Boolean(dealershipId),
  });
  const rooftops = rooftopsQuery.data ?? [];

  const verifyMutation = useMutation({
    mutationFn: () =>
      adminApi.verifyIntegrationToken(dealershipId, {
        provider: form.values.provider,
        token: form.values.token,
      }),
    onSuccess: (resp) => {
      setVerified(resp.dealership);
      setVerifyError(null);
    },
    onError: (err: unknown) => {
      setVerified(null);
      // The backend returns the user-facing string verbatim in
      // `message`. We trust it (it's the user-stated phrasing).
      setVerifyError(err instanceof Error ? err.message : String(err));
    },
  });

  const mutation = useMutation({
    mutationFn: (input: CreateIntegration) =>
      adminApi.createIntegration(dealershipId, input),
    onSuccess: (row) => {
      onCreated(row);
      onOpenChange(false);
      form.reset();
      setVerified(null);
      setVerifyError(null);
      setMapping({});
      setScopeRooftopId(ALL_LOCATIONS);
    },
    onError: (err: unknown) => {
      toast.error(
        `Create failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = form.validate();
    if (!result.ok) return;
    // For Space Auto, require a successful verify before the operator
    // can save the integration. ADF doesn't have a verify step.
    if (result.data.provider === "spaceauto" && !verified) {
      setVerifyError(
        'Please click "Sync & verify" first to confirm the token and pick a Space Auto location for each rooftop.',
      );
      return;
    }
    // The sync files customers/appointments under rooftops via this
    // mapping, so enforce what the label promises: every rooftop mapped.
    if (result.data.provider === "spaceauto") {
      const unmapped = rooftops.filter((r) => !mapping[r.id]);
      if (unmapped.length > 0) {
        setVerifyError(
          `Pick a Space Auto location for every rooftop (missing: ${unmapped
            .map((r) => r.name)
            .join(", ")}).`,
        );
        return;
      }
      setVerifyError(null);
    }
    const cleanedMapping: IntegrationLocationsMapping =
      Object.keys(mapping).length > 0 ? mapping : null;
    mutation.mutate({
      ...result.data,
      rooftopId: scopeRooftopId === ALL_LOCATIONS ? null : scopeRooftopId,
      includeUnauthorized: result.data.includeUnauthorized ?? false,
      notes: result.data.notes?.length ? result.data.notes : undefined,
      locationsMapping: cleanedMapping,
      startSyncAt: result.data.startSyncAt ?? null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
          <DialogHeader>
            <DialogTitle>Add integration</DialogTitle>
            <DialogDescription>
              Connect Space Auto for two-way customer + activity sync, or
              ADF to forward new leads to a CRM by email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 my-4 overflow-y-auto pr-1">
            <FormField label="Provider">
              <Select
                value={form.values.provider}
                onValueChange={(v) =>
                  form.setField("provider", v as IntegrationProvider)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IntegrationProviderSchema.options.map((p) => (
                    <SelectItem key={p} value={p}>
                      {IntegrationProviderLabels[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label={
                form.values.provider === "adf"
                  ? "Recipient email"
                  : "Space Auto API key"
              }
              error={form.errors.token}
            >
              <div className="flex gap-2">
                <Input
                  value={form.values.token}
                  onChange={(e) => {
                    form.setField("token", e.target.value);
                    // Re-entering the token invalidates the previous verify.
                    if (verified) {
                      setVerified(null);
                      setVerifyError(null);
                      setMapping({});
                    }
                  }}
                  type={form.values.provider === "adf" ? "email" : "password"}
                  placeholder={
                    form.values.provider === "adf"
                      ? "crm-leads@example.com"
                      : "paste API key"
                  }
                />
                {form.values.provider === "spaceauto" ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => verifyMutation.mutate()}
                    disabled={
                      verifyMutation.isPending ||
                      form.values.token.trim().length === 0
                    }
                  >
                    {verifyMutation.isPending ? "Verifying…" : "Sync & verify"}
                  </Button>
                ) : null}
              </div>
            </FormField>

            {form.values.provider === "adf" ? (
              <FormField
                label="Location"
                description="Send leads from one location only, or from the whole dealership."
              >
                <Select
                  value={scopeRooftopId}
                  onValueChange={setScopeRooftopId}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_LOCATIONS}>All locations</SelectItem>
                    {rooftops.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            ) : null}

            {form.values.provider === "spaceauto" && verifyError ? (
              <p className="text-sm text-destructive">{verifyError}</p>
            ) : null}

            {form.values.provider === "spaceauto" && verified ? (
              <FormField
                label={`Verified dealership: ${verified.name ?? "(unnamed)"} — map Lumika rooftops to Space Auto locations`}
                description="Each Lumika rooftop must be mapped to a Space Auto location so the sync places customers, appointments, and trade-ins under the right rooftop."
              >
                <div className="space-y-2">
                  {rooftops.map((r) => (
                    <div
                      key={r.id}
                      className="grid grid-cols-1 items-center gap-2 sm:grid-cols-2"
                    >
                      <span className="text-sm font-medium">{r.name}</span>
                      <Select
                        value={mapping[r.id] ?? ""}
                        onValueChange={(v) =>
                          setMapping((m) => ({ ...m, [r.id]: v }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pick a Space Auto location" />
                        </SelectTrigger>
                        <SelectContent>
                          {verified.locations.map((l) => (
                            <SelectItem key={l.id} value={l.name}>
                              {l.name}
                              {l.timezone ? ` — ${l.timezone}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </FormField>
            ) : null}

            {form.values.provider === "spaceauto" ? (
              <FormField
                label="Sync customers since (optional)"
                description="Leave blank to import only customers updated after the integration is enabled. Set a date to also import the history of every customer created since then (one-time backfill, runs on the next hourly tick)."
                error={form.errors.startSyncAt}
              >
                <Input
                  type="date"
                  value={
                    form.values.startSyncAt
                      ? form.values.startSyncAt.slice(0, 10)
                      : ""
                  }
                  onChange={(e) =>
                    form.setField(
                      "startSyncAt",
                      e.target.value
                        ? new Date(e.target.value).toISOString()
                        : null,
                    )
                  }
                />
              </FormField>
            ) : null}
            <FormField label="Notes (optional)" error={form.errors.notes}>
              <Textarea
                value={form.values.notes ?? ""}
                onChange={(e) => form.setField("notes", e.target.value)}
                placeholder="Reminder of what this connection is for"
              />
            </FormField>
            <FormField
              label="Include anonymous contacts"
              description="We do not recommend switching it on. Do not include until you are 100% sure you need it."
            >
              <Switch
                checked={form.values.includeUnauthorized ?? false}
                onCheckedChange={(v) =>
                  form.setField("includeUnauthorized", v)
                }
                aria-label="Include anonymous contacts"
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
